import dsBridge from './dsbridge'
import { DeviceTypeEnum } from './enums/device'
import { getItemFileType } from './file-types'
import { getClientEnv, goToUrl, isApp } from './utils'

/**
 * Convert File/Blob to base64 string for Android intent
 * Returns base64 encoded string
 */
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export const baseBridgeFn = (dsbridgeCb: any, dkbridgeCb: any, webCb?: any) => {
  const env = getClientEnv()
  switch (env) {
    case DeviceTypeEnum.IOS:
      return dsbridgeCb
    case DeviceTypeEnum.ANDROID:
      return dkbridgeCb
    default:
      return webCb ? webCb : dkbridgeCb
  }
}

export const commonBridgeFn = (fnName: string, isAsync: boolean, data = {}) => {
  const _args = JSON.stringify({ data })

  const dkBridgeFn = window.dkBridge?.[fnName]?.(_args)
  if (isAsync) {
    const dsBridgeCb = new Promise((resolve) => {
      dsBridge.call(`${fnName}`, data, (json: any) => {
        resolve(json)
      })
    })
    return baseBridgeFn(dsBridgeCb, dkBridgeFn)
  }
  return baseBridgeFn(dsBridge.call(`${fnName}`, data), dkBridgeFn)
}

export const isEnableUseDsbridge = () => {
  const env = getClientEnv()
  switch (env) {
    case DeviceTypeEnum.IOS:
      return true
    case DeviceTypeEnum.ANDROID:
      return false
    default:
      break
  }
}

export const goBackByNative = () => {
  if (window.history.length <= 1) {
    commonBridgeFn('finish', false)
  }
}

export const registerGoback = () => {
  if (isEnableUseDsbridge()) {
    dsBridge.register('goBack', () => {
      goBackByNative()
    })
  } else {
    window.dkBridge.goBack = () => {
      goBackByNative()
    }
  }
}

const MIN_APP_VERSION = {
  IOS_INTENT_SUPPORT: 415,
  IOS_VIDEO_SUPPORT: 415,
  ANDROID_INTENT_SUPPORT: 337,
  ANDROID_FILE_CHOOSER_SUPPORT: 327,
}

export const checkUploadSupported = (): boolean => {
  const env = getClientEnv()
  if (env !== DeviceTypeEnum.ANDROID) return true
  return isAndroidVersionSupported(MIN_APP_VERSION.ANDROID_FILE_CHOOSER_SUPPORT)
}

const buildPreviewUrl = (fileUrl: string, fileName: string, fileType: string) => {
  const previewPath = `/ai-bot/preview?url=${encodeURIComponent(fileUrl)}&name=${encodeURIComponent(fileName)}&type=${encodeURIComponent(fileType)}`
  return {
    path: previewPath,
    fullUrl: `${window.location.origin}${previewPath}`,
  }
}

const isIOSVersionSupported = (minAppVersion: number): boolean => {
  const appVersionCode = commonBridgeFn('getAppVersionCode', false) as number
  if (!appVersionCode || appVersionCode < minAppVersion) {
    return false
  }
  return true
}

const isAndroidVersionSupported = (minAppVersion: number): boolean => {
  const appVersionCode = window.dkBridge?.getAppVersionCode?.()
  if (!appVersionCode || appVersionCode < minAppVersion) {
    return false
  }
  return true
}

const convertBlobToBase64 = async (fileUrl: string): Promise<string | undefined> => {
  try {
    const response = await fetch(fileUrl)
    const blob = await response.blob()
    return await fileToBase64(blob)
  } catch (e) {
    console.error('Failed to convert file to base64:', e)
    return undefined
  }
}

const handleOpenDocNative = async (
  env: DeviceTypeEnum,
  fileUrl: string,
  fileName: string,
  fileType: string,
) => {
  const fileBase64 = await convertBlobToBase64(fileUrl)
  if (env === DeviceTypeEnum.IOS) {
    commonBridgeFn('openBase64WebView', false, {
      fileType,
      fileName,
      fileBase64,
    })
  } else {
    window.dkBridge?.handleIntent?.(fileType, fileName, fileBase64)
  }
}

export const checkOpenPreviewSupported = (fileUrl: string, fileType: string) => {
  const env = getClientEnv()
  const isLocalFile = fileUrl.startsWith('blob:')
  if (env === DeviceTypeEnum.IOS && fileType.includes('pdf') && isLocalFile) {
    return isIOSVersionSupported(MIN_APP_VERSION.IOS_INTENT_SUPPORT)
  } else if (env === DeviceTypeEnum.ANDROID && getItemFileType(fileType) === 'document' && isLocalFile) {
    return isAndroidVersionSupported(MIN_APP_VERSION.ANDROID_INTENT_SUPPORT)
  }
  return true
}

const handleNativeAppPreview = async (
  env: DeviceTypeEnum,
  fileUrl: string,
  fileName: string,
  fileType: string,
  previewUrl: string,
) => {
  const isLocalFile = fileUrl.startsWith('blob:')
  const isDocument = getItemFileType(fileType) === 'document'

  // Use native intent for IOS pdf & Android document
  if (isLocalFile && (env === DeviceTypeEnum.IOS && fileType.includes('pdf') || (env === DeviceTypeEnum.ANDROID && isDocument))) {
    await handleOpenDocNative(env, fileUrl, fileName, fileType)
    return
  } else {
    goToUrl(previewUrl)
  }
}

const handleWebPreview = (previewPath: string, onWebFallback?: () => void) => {
  if (onWebFallback) {
    onWebFallback()
  } else {
    window.location.href = previewPath
  }
}

export const openFilePreview = async (
  fileUrl: string,
  fileName: string,
  fileType: string,
  onWebFallback?: () => void,
) => {
  const env = getClientEnv()
  const { path: previewPath, fullUrl: previewUrl } = buildPreviewUrl(fileUrl, fileName, fileType)

  if (env === DeviceTypeEnum.H5) {
    handleWebPreview(previewPath, onWebFallback)
  } else {
    await handleNativeAppPreview(env, fileUrl, fileName, fileType, previewUrl)
  }
}

export const closeFilePreview = (onWebFallback?: () => void) => {
  if (isApp()) {
    window.dkBridge?.finish?.()
    dsBridge.call('finish')
  } else {
    if (onWebFallback) {
      onWebFallback()
    } else {
      window.history.back()
    }
  }
}

export const acceptInputFileTypes = (): string => {
  const env = getClientEnv()
  const isIOS = env === DeviceTypeEnum.IOS
  const acceptAllFileTypes = 'image/png, image/jpg, image/jpeg, video/mp4, .mov, video/quicktime, .pdf, .doc, .docx, .heic, .hevc'
  const acceptWithoutVideoFileTypes = 'image/png, image/jpg, image/jpeg, .pdf, .doc, .docx, .heic'
  if (!isIOS) {
    return acceptAllFileTypes
  }
  const AppVersionCode = commonBridgeFn('getAppVersionCode', false)
  const videoSuppertedVersionCode = MIN_APP_VERSION.IOS_VIDEO_SUPPORT
  return AppVersionCode < videoSuppertedVersionCode ? acceptWithoutVideoFileTypes : acceptAllFileTypes
}